import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useCycle } from '@/lib/cycle';
import { PageHeader, Card, KPI, Loading, Empty, SectionBadge } from '@/components/UI';
import { ManpowerProductivityCard, OpsSnapshotSection } from '@/components/OpsSnapshot';

// The Operations KPI export is pre-aggregated to three read-only routes — /api/kpi/scopes,
// /api/kpi/export (pre-aggregated metrics) and /api/kpi/masters (parts,
// customers, operations, units). Verified against the live server: no route
// available to an API key returns per-customer, per-part, per-defect,
// per-breakdown or tool-life rows. So for an API-synced month an empty
// drill-down table means the export does not carry those rows — it must not
// read as "none recorded", and it is not something a different fetch can fix.
const NO_ROWS_NOTE = 'Not included in the Operations KPI export — its KPI export returns aggregate metrics only; row-level records are not exposed to API keys.';
function emptyNote(data, whenNone, apiNote) {
  return data?.detail_rows_available === false ? (apiNote || NO_ROWS_NOTE) : whenNone;
}
const isOpsMonth = (data) => data?.source === 'ops_api';

// Same thresholds the JSON parser applies to customer rows, so shop rows from
// the API are coloured on an identical scale.
const adherenceTone = (p) => (p >= 95 ? 'green' : p >= 80 ? 'amber' : 'red');

import { formatDateTime, formatNumber } from '@/lib/format';
import { Upload, ChevronDown, ChevronRight, AlertTriangle, Wrench, ShieldAlert } from 'lucide-react';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatCurrency = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '–';
  return '₹' + Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

const formatHours = (h) => {
  if (h === null || h === undefined) return '–';
  return `${formatNumber(h)} hrs`;
};

const formatPct = (p) => {
  if (p === null || p === undefined) return '–';
  return `${Number(p).toFixed(1)}%`;
};

// Format strokes in Indian numbering system (lakhs/crores with commas)
// e.g. 6265586 -> "62,65,586" ; 1200000 -> "12,00,000"
const formatIndian = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '–';
  return Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

// ---------------------------------------------------------------------------
// Section components
// ---------------------------------------------------------------------------

// Shared inline styles to guarantee <th>/<td> column alignment in the
// breakdown tables (Tailwind utility classes were sometimes overridden by
// the global `aampl-table` rules, causing header/cell drift).
const CELL_LEFT = { textAlign: 'left', padding: '12px 16px', verticalAlign: 'top' };
const CELL_RIGHT = { textAlign: 'right', padding: '12px 16px', verticalAlign: 'top', whiteSpace: 'nowrap' };

function BreakdownLabelCell({ row }) {
  const label = [row.customer, row.part_name || row.part, row.operation || row.tool]
    .filter((x) => x && x !== '-')
    .join(' / ');
  const subParts = [];
  if (row.part_no && row.part_no !== '-') subParts.push(row.part_no);
  // The KPI export labels a breakdown by its issue text only, so don't repeat it.
  if (row.top_description && row.top_description !== '-' && row.top_description !== label) subParts.push(row.top_description);
  return (
    <td style={CELL_LEFT}>
      <div className="font-medium">{label || row.top_description || '-'}</div>
      {subParts.length > 0 && (
        <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{subParts.join(' • ')}</div>
      )}
    </td>
  );
}


// Press shop vs machine shop, reported separately by the Operations KPI export.
// Absent for JSON-sourced months, in which case the card does not render.
function ShopWiseCard({ ops }) {
  if (!ops) return null;
  const strokes = ops.press_shop_strokes || {};
  const hasStrokes = (strokes.total_strokes || 0) > 0 || (strokes.pieces || 0) > 0;
  const eff = ops.overall_efficiency_pct;
  const hasEff = eff !== null && eff !== undefined && Number(eff) > 0;
  const shops = [
    { key: 'press', label: 'Press Shop', plan: ops.press_shop_plan_qty, actual: ops.press_shop_actual_qty, pct: ops.press_shop_completion_pct },
    { key: 'machine', label: 'Machine Shop', plan: ops.machine_shop_plan_qty, actual: ops.machine_shop_actual_qty, pct: ops.machine_shop_completion_pct },
    { key: 'inspection', label: 'Final Inspection', plan: ops.inspection_plan_qty, actual: ops.inspection_actual_qty, pct: ops.inspection_completion_pct },
  ].filter((s) => s.plan || s.actual);
  if (!shops.length) return null;
  const tone = (p) => (p >= 95 ? 'green' : p >= 80 ? 'amber' : 'red');
  return (
    <Card title="Shop-wise Production" action={<span className="text-xs text-slate-500">From Operations KPI export</span>}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="shopwise-cards">
        {shops.map((sh) => (
          <div key={sh.key} className="border border-[var(--aampl-border)] rounded px-3 py-3">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">{sh.label}</div>
            <div className="flex items-baseline justify-between mt-1">
              <span className="text-xs text-slate-600">Plan</span>
              <span className="font-semibold">{formatNumber(sh.plan)}</span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-xs text-slate-600">Actual</span>
              <span className="font-semibold">{formatNumber(sh.actual)}</span>
            </div>
            <div className="flex items-baseline justify-between mt-1 pt-1 border-t border-[var(--aampl-border)]">
              <span className="text-xs text-slate-600">Completion</span>
              <SectionBadge status={tone(sh.pct)} label={`${sh.pct ?? 0}%`} />
            </div>
          </div>
        ))}
      </div>
      {(hasEff || hasStrokes) && (
        <div className="mt-4" data-testid="shopwise-strokes">
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 text-xs">
            {[
              hasEff && { label: 'Overall efficiency', value: `${Number(eff).toFixed(1)}%` },
              hasStrokes && { label: 'Total strokes', value: formatIndian(strokes.total_strokes) },
              hasStrokes && { label: 'Press strokes', value: formatIndian(strokes.press_strokes) },
              hasStrokes && { label: 'Welding strokes', value: formatIndian(strokes.welding_strokes) },
              hasStrokes && { label: 'Pieces (strokes × cavities)', value: formatIndian(strokes.pieces) },
              hasStrokes && { label: 'Days with entries', value: formatIndian(strokes.days_with_entries) },
            ].filter(Boolean).map((m) => (
              <div key={m.label} className="border border-[var(--aampl-border)] rounded px-3 py-2 flex items-baseline justify-between gap-2">
                <span className="text-slate-600">{m.label}</span>
                <span className="font-semibold aampl-navy-text">{m.value}</span>
              </div>
            ))}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">
            Overall efficiency = Σ(strokes × cavities) ÷ Σ(daily target × days in period) across press + welding operations, as the Operations Production Summary computes it.
          </div>
        </div>
      )}
    </Card>
  );
}


// Plant-level rejection / rework / scrap with the PPM figures the Operations KPI export
// computes itself. Only rendered for API-sourced months — the JSON parsers do
// not produce PPM, so there is nothing to show for uploaded backups.
function RejectionPpmCard({ q }) {
  const r = q?.rejection_summary;
  if (!r || !(r.rejection_qty || r.rework_qty || r.scrap_qty)) return null;
  const ppmTone = (v) => (v >= 25000 ? 'red' : v >= 10000 ? 'amber' : 'green');
  return (
    <Card
      title="Rejection & PPM"
      action={<span className="text-xs text-slate-500">Plant-wide • From Operations KPI export</span>}
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4" data-testid="rejection-kpis">
        <KPI label="Rejection Qty" value={formatNumber(r.rejection_qty)} status="red" />
        <KPI label="Rework Qty" value={formatNumber(r.rework_qty)} status="amber" />
        <KPI label="Scrap Qty" value={formatNumber(r.scrap_qty)} status="amber" />
        <KPI label="Plant PPM" value={formatNumber(r.plant_ppm)} status={ppmTone(r.plant_ppm)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">PPM Breakdown</div>
          <div className="aampl-table-wrap">
            <table className="aampl-table" data-testid="ppm-table">
              <thead><tr><th>Measure</th><th style={{ textAlign: 'right' }}>Qty</th><th style={{ textAlign: 'right' }}>PPM</th></tr></thead>
              <tbody>
                <tr><td>Rejection</td><td style={{ textAlign: 'right' }}>{formatNumber(r.rejection_qty)}</td><td style={{ textAlign: 'right' }}>{formatNumber(r.rejection_ppm)}</td></tr>
                <tr><td>Rework</td><td style={{ textAlign: 'right' }}>{formatNumber(r.rework_qty)}</td><td style={{ textAlign: 'right' }}>{formatNumber(r.rework_ppm)}</td></tr>
                <tr><td>Scrap</td><td style={{ textAlign: 'right' }}>{formatNumber(r.scrap_qty)}</td><td style={{ textAlign: 'right' }}>{formatNumber(r.scrap_ppm)}</td></tr>
                <tr style={{ fontWeight: 600 }}><td>Plant total</td><td style={{ textAlign: 'right' }}>–</td><td style={{ textAlign: 'right' }}>{formatNumber(r.plant_ppm)}</td></tr>
              </tbody>
            </table>
          </div>
          <div className="text-[11px] text-slate-500 mt-2">
            Base qty {formatNumber(r.ppm_base_qty)}{r.ppm_basis_note ? ` — ${r.ppm_basis_note}` : ''}
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">COPQ by Area</div>
          <div className="aampl-table-wrap">
            <table className="aampl-table" data-testid="copq-area-table">
              <thead><tr><th>Area</th><th style={{ textAlign: 'right' }}>Scrap Qty</th><th style={{ textAlign: 'right' }}>COPQ</th></tr></thead>
              <tbody>
                {(r.copq_by_area || []).map((a) => (
                  <tr key={a.area}>
                    <td style={{ textTransform: 'capitalize' }}>{a.area.replace(/_/g, ' ')}</td>
                    <td style={{ textAlign: 'right' }}>{formatNumber(a.scrap_qty)}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(a.copq)}</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 600 }}>
                  <td>Total</td><td style={{ textAlign: 'right' }}>–</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(r.copq_total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Card>
  );
}

function ToolMaintenanceCard({ tb, data }) {
  if (!tb) return null;
  // Prefer server-computed lists. Fall back to deriving them from the legacy
  // `top_breakdowns` array (which contains operation-level aggregates) so old
  // parsed_data created before V2.2 still renders both views correctly.
  const legacy = tb.top_breakdowns || [];
  const sortByCount = (arr) => [...arr].sort((a, b) => (b.count || 0) - (a.count || 0) || (b.total_downtime || 0) - (a.total_downtime || 0));
  const sortByDowntime = (arr) => [...arr].sort((a, b) => (b.total_downtime || 0) - (a.total_downtime || 0) || (b.count || 0) - (a.count || 0));
  const topByCount = (tb.top_by_count && tb.top_by_count.length ? tb.top_by_count : sortByCount(legacy)).slice(0, 3);
  const topByDowntime = (tb.top_by_downtime && tb.top_by_downtime.length ? tb.top_by_downtime : sortByDowntime(legacy)).slice(0, 3);
  // With zero breakdowns in the month the Operations KPI export returns null for the
  // month MTTR/MTBF (mean of nothing). Show "–" rather than a misleading 0 hrs.
  const hasMonthRate = !isOpsMonth(data) || (tb.total_count || 0) > 0;
  const toolsSummary = data?.ops_metrics?.tools_summary || {};
  const technicians = tb.technicians || [];
  const records = tb.records_sample || [];
  // The hr-kpi export carries approved breakdown records; the older metrics-only
  // export does not. Only claim "not included" when the parser saw no record list.
  const recordsCarried = tb.records_total !== undefined;

  return (
    <Card
      title="Tool Maintenance Summary"
      action={
        <span className="text-xs text-slate-500" data-testid="tool-maint-source">
          {data?.source === 'ops_api'
            ? 'From Operations KPI export • tool maintenance (excluding preventive)'
            : 'From Production JSON • maintenance_records (excluding preventive)'}
        </span>
      }
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <KPI label="Total Breakdowns" value={tb.total_count || 0} status="navy" />
        <KPI label="Total Downtime" value={formatHours(tb.total_downtime_hours)} status="amber" />
        <KPI
          label="MTTR (month)"
          value={hasMonthRate && tb.mttr !== null && tb.mttr !== undefined ? formatHours(tb.mttr) : '–'}
          status="amber"
        />
        <KPI
          label="MTBF (month, shift-aware)"
          value={hasMonthRate && tb.mtbf !== null && tb.mtbf !== undefined ? formatHours(tb.mtbf) : '–'}
          status={!hasMonthRate ? 'green' : tb.mtbf && tb.mtbf >= 200 ? 'green' : tb.mtbf && tb.mtbf >= 100 ? 'amber' : 'red'}
        />
      </div>

      {/* Remaining tool-maintenance figures the Operations KPI export computes itself.
          The API names tool_mttr_hours_overall / tool_mtbf_hours_overall
          "MTTR (all-time)" / "MTBF (all-time)": they are cumulative over the unit's
          whole history, identical for every month, and so can be non-zero in a
          month with no breakdowns. */}
      {isOpsMonth(data) && data?.ops_metrics && (
        <div className="mb-4" data-testid="tool-maint-ops-extra">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
            {[
              { label: 'Polishing Count (month)', value: formatNumber(data.ops_metrics.tool_polishing_count ?? 0) },
              { label: 'Preventive Count (month)', value: formatNumber(tb.preventive_count ?? 0) },
              { label: 'MTTR (all-time)', value: formatHours(data.ops_metrics.tool_mttr_hours_overall) },
              { label: 'MTBF (all-time)', value: formatHours(data.ops_metrics.tool_mtbf_hours_overall) },
              tb.pm_checksheets_filled !== undefined && { label: 'PM check sheets filled', value: formatNumber(tb.pm_checksheets_filled) },
              toolsSummary.tracked !== undefined && { label: 'Tools tracked (live)', value: formatNumber(toolsSummary.tracked) },
              toolsSummary.overdue_pm !== undefined && { label: 'Tools overdue PM (live)', value: formatNumber(toolsSummary.overdue_pm) },
              toolsSummary.never_pmd !== undefined && { label: 'Tools never PM’d (live)', value: formatNumber(toolsSummary.never_pmd) },
              toolsSummary.renewals_this_month !== undefined && { label: 'Tool renewals (month)', value: formatNumber(toolsSummary.renewals_this_month) },
            ].filter(Boolean).map((m) => (
              <div key={m.label} className="border border-[var(--aampl-border)] rounded px-3 py-2 flex items-baseline justify-between">
                <span className="text-slate-600">{m.label}</span>
                <span className="font-semibold aampl-navy-text">{m.value}</span>
              </div>
            ))}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">
            All-time MTTR/MTBF are cumulative figures the Operations KPI export computes over the unit&apos;s full breakdown history; they do not change month to month.
          </div>
        </div>
      )}

      {topByCount.length === 0 && topByDowntime.length === 0 ? (
        <div className="text-sm text-slate-500 py-4 text-center" data-testid="tool-maint-empty">
          {isOpsMonth(data) && (!(tb.total_count > 0) || recordsCarried)
            ? 'No tool breakdowns recorded in the Operations KPI export for this period.'
            : emptyNote(data, 'No tool breakdowns recorded this month.',
              'Individual breakdown records (tool, date, reason, duration) are not included in the Operations KPI export — its export carries only the totals above, so a Top 5 list cannot be built from it.')}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* ----- Top 3 by COUNT ----- */}
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">
              Top 3 by Breakdown Count
            </div>
            {topByCount.length === 0 ? (
              <div className="text-sm text-slate-500 py-4 text-center bg-slate-50 rounded">No data.</div>
            ) : (
              <div className="aampl-table-wrap">
                <table
                  style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}
                  data-testid="tool-maint-top-count-table"
                >
                  <colgroup>
                    <col style={{ width: '65%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '20%' }} />
                  </colgroup>
                  <thead>
                    <tr className="aampl-thead-row">
                      <th style={CELL_LEFT}>CUSTOMER / PART / OPERATION</th>
                      <th style={CELL_RIGHT}>COUNT</th>
                      <th style={CELL_RIGHT}>TOP REASON</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topByCount.slice(0, 3).map((row, i) => (
                      <tr key={i}>
                        <BreakdownLabelCell row={row} />
                        <td style={CELL_RIGHT}><strong>{row.count}</strong></td>
                        <td style={{ ...CELL_RIGHT, fontSize: '0.75rem', whiteSpace: 'normal' }}>
                          {row.top_description && row.top_description !== (row.operation || row.tool) ? row.top_description : '–'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ----- Top 3 by DOWNTIME ----- */}
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">
              Top 3 by Downtime Hours
            </div>
            {topByDowntime.length === 0 ? (
              <div className="text-sm text-slate-500 py-4 text-center bg-slate-50 rounded">No data.</div>
            ) : (
              <div className="aampl-table-wrap">
                <table
                  style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}
                  data-testid="tool-maint-top-downtime-table"
                >
                  <colgroup>
                    <col style={{ width: '50%' }} />
                    <col style={{ width: '28%' }} />
                    <col style={{ width: '22%' }} />
                  </colgroup>
                  <thead>
                    <tr className="aampl-thead-row">
                      <th style={CELL_LEFT}>CUSTOMER / PART / OPERATION</th>
                      <th style={CELL_RIGHT}>TOTAL DOWNTIME</th>
                      <th style={CELL_RIGHT}>AVG REPAIR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topByDowntime.slice(0, 3).map((row, i) => (
                      <tr key={i}>
                        <BreakdownLabelCell row={row} />
                        <td style={CELL_RIGHT}><strong>{formatHours(row.total_downtime)}</strong></td>
                        <td style={CELL_RIGHT}>{formatHours(row.avg_repair)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {(technicians.length > 0 || records.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {technicians.length > 0 && (
            <div data-testid="tool-maint-technicians">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Breakdowns by Technician</div>
              <div className="aampl-table-wrap">
                <table className="aampl-table text-xs" data-testid="tool-maint-technicians-table">
                  <thead><tr><th>Technician</th><th className="text-right">Breakdowns</th><th className="text-right">Downtime</th><th className="text-right">Avg / breakdown</th></tr></thead>
                  <tbody>
                    {technicians.slice(0, 8).map((t) => (
                      <tr key={t.name}>
                        <td className="font-medium">{t.name}</td>
                        <td className="text-right"><strong>{formatNumber(t.breakdowns)}</strong></td>
                        <td className="text-right">{formatHours(t.downtime_hours)}</td>
                        <td className="text-right">{t.breakdowns ? formatHours(Math.round(t.downtime_hours / t.breakdowns * 100) / 100) : '–'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Multi-person entries credit each technician with the full breakdown and downtime, so the column totals can exceed the unit totals.
              </div>
            </div>
          )}
          {records.length > 0 && (
            <div data-testid="tool-maint-records">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">
                Recent Breakdown Records{tb.records_total > records.length ? ` (${records.length} of ${tb.records_total})` : ''}
              </div>
              <div className="aampl-table-wrap" style={{ maxHeight: 320, overflowY: 'auto' }}>
                <table className="aampl-table text-xs" data-testid="tool-maint-records-table">
                  <thead><tr><th>Opened</th><th>Issue</th><th className="text-right">Downtime</th><th>Technician</th></tr></thead>
                  <tbody>
                    {records.map((r, i) => (
                      <tr key={i}>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(r.date)}</td>
                        <td className="font-medium" style={{ whiteSpace: 'normal' }}>{r.reason || r.tool || '-'}</td>
                        <td className="text-right">{formatHours(r.downtime_hours)}</td>
                        <td>{r.technician || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {tb.preventive_count !== undefined && tb.preventive_count > 0 && (
        <div className="text-xs text-slate-500 mt-4">
          <Wrench size={12} className="inline mr-1" />
          {tb.preventive_count} preventive maintenance record{tb.preventive_count === 1 ? '' : 's'} this month (excluded from breakdown KPIs).
        </div>
      )}
    </Card>
  );
}

function ToolLifeAlertsCard({ tl, data }) {
  if (!tl) return null;
  const past = tl.past_life || [];
  const approaching = tl.approaching_life || [];

  if (!tl.total_tools_tracked) {
    const ops = isOpsMonth(data);
    return (
      <Card
        title="Tool Life Alerts"
        action={<span className="text-xs text-slate-500" data-testid="tool-life-source">{ops ? 'Not in Operations KPI export export' : 'No tools tracked'}</span>}
      >
        <div className="text-sm text-slate-500 py-4 text-center" data-testid="tool-life-empty">
          {emptyNote(data, 'No operations have a configured tool life limit.',
            'Tool life is not included in the Operations KPI export — its export has no tool-life metric and its operations master exposes no tool life limit, current usage or renewal fields, so end-of-life alerts cannot be derived from it.')}
        </div>
      </Card>
    );
  }

  const renderRow = (row, i, tone) => {
    const label = [row.customer, row.part, row.operation]
      .filter((x) => x && x !== '-')
      .join(' / ');
    const pctClass = tone === 'red' ? 'text-red-700' : 'text-amber-700';
    return (
      <tr key={i}>
        <td className="font-medium" style={{ whiteSpace: 'normal' }}>
          {label || row.operation || '-'}
          {row.part_no && row.part_no !== '-' && (
            <div className="text-[10px] text-slate-500">{row.part_no}</div>
          )}
        </td>
        <td className="text-right">{formatIndian(row.tool_life_limit)}</td>
        <td className="text-right">{formatIndian(row.cumulative_strokes)}</td>
        <td className={`text-right font-bold ${pctClass}`}>{formatPct(row.usage_pct)}</td>
      </tr>
    );
  };

  const tableHead = (
    <colgroup>
      <col style={{ width: '52%' }} />
      <col style={{ width: '16%' }} />
      <col style={{ width: '16%' }} />
      <col style={{ width: '16%' }} />
    </colgroup>
  );

  return (
    <Card
      title="Tool Life Alerts"
      action={
        <span className="text-xs text-slate-500" data-testid="tool-life-source">
          From operations.tool_life_limit + daily_entries
        </span>
      }
    >
      {/* KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <KPI label="Tracked Tools" value={formatIndian(tl.total_tools_tracked)} status="navy" />
        <KPI label="Critical (>100%)" value={formatIndian(tl.total_past_life)} status={tl.total_past_life > 0 ? 'red' : 'green'} />
        <KPI label="Warning (80–100%)" value={formatIndian(tl.total_approaching)} status={tl.total_approaching > 0 ? 'amber' : 'green'} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Past life */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <ShieldAlert size={16} className="text-red-600" />
              <h4 className="font-semibold text-sm aampl-navy-text">Past Tool Life (Critical)</h4>
            </div>
            <SectionBadge status="red" label={`${tl.total_past_life} ${tl.total_past_life === 1 ? 'tool' : 'tools'}`} />
          </div>
          {past.length === 0 ? (
            <div className="text-sm text-slate-500 py-4 text-center bg-slate-50 rounded">
              No tools past their life limit. ✓
            </div>
          ) : (
            <div className="aampl-table-wrap" style={{ borderLeft: '3px solid #dc2626' }}>
              <table
                className="aampl-table text-xs"
                data-testid="past-life-table"
                style={{ tableLayout: 'fixed', width: '100%' }}
              >
                {tableHead}
                <thead>
                  <tr>
                    <th>Customer / Part / Operation</th>
                    <th className="text-right">Life Limit</th>
                    <th className="text-right">Current</th>
                    <th className="text-right">Usage %</th>
                  </tr>
                </thead>
                <tbody>{past.map((row, i) => renderRow(row, i, 'red'))}</tbody>
              </table>
            </div>
          )}
          {tl.total_past_life > past.length && (
            <div className="text-[11px] text-slate-500 mt-2" data-testid="past-life-more">
              Showing top {past.length} of {tl.total_past_life}.
            </div>
          )}
        </div>

        {/* Approaching life */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-amber-600" />
              <h4 className="font-semibold text-sm aampl-navy-text">Approaching End of Life (Warning)</h4>
            </div>
            <SectionBadge status="amber" label={`${tl.total_approaching} ${tl.total_approaching === 1 ? 'tool' : 'tools'}`} />
          </div>
          {approaching.length === 0 ? (
            <div className="text-sm text-slate-500 py-4 text-center bg-slate-50 rounded">
              No tools approaching end-of-life (80–100%).
            </div>
          ) : (
            <div className="aampl-table-wrap" style={{ borderLeft: '3px solid #d97706' }}>
              <table
                className="aampl-table text-xs"
                data-testid="approaching-life-table"
                style={{ tableLayout: 'fixed', width: '100%' }}
              >
                {tableHead}
                <thead>
                  <tr>
                    <th>Customer / Part / Operation</th>
                    <th className="text-right">Life Limit</th>
                    <th className="text-right">Current</th>
                    <th className="text-right">Usage %</th>
                  </tr>
                </thead>
                <tbody>{approaching.map((row, i) => renderRow(row, i, 'amber'))}</tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

function QualitySummaryCard({ q, data }) {
  if (!q) return null;
  const hasData = (q.entries_counted || 0) > 0 || (q.total_rejection_qty || 0) > 0;
  if (!hasData) {
    return (
      <Card title="Quality / COPQ Summary">
        <div className="text-sm text-slate-500 py-6 text-center" data-testid="quality-empty">
          Quality data not available for this month.
        </div>
      </Card>
    );
  }
  return (
    <Card
      title="Quality / COPQ Summary"
      action={<span className="text-xs text-slate-500">{data?.source === 'ops_api' ? 'From Operations KPI export' : 'From defect entries'}</span>}
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <KPI label="Total COPQ" value={formatCurrency(q.total_copq)} status="red" />
        <KPI label="Total Scrap Qty" value={formatNumber(q.total_scrap_qty)} status="amber" />
        {data?.source === 'ops_api'
          ? <KPI label="Total Rejection Qty" value={formatNumber(q.total_rejection_qty)} status="navy" />
          : <KPI label="Rejection Entries" value={formatNumber(q.entries_counted)} status="navy" />}
      </div>

      {(q.fg_dispatch_value_inr > 0 || q.red_tag_pct_of_dispatch_value !== null && q.red_tag_pct_of_dispatch_value !== undefined) && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-xs mb-4" data-testid="quality-dispatch-extra">
          {[
            q.fg_dispatch_value_inr > 0 && { label: 'FG dispatch value (month)', value: formatCurrency(q.fg_dispatch_value_inr) },
            q.red_tag_pct_of_dispatch_value !== null && q.red_tag_pct_of_dispatch_value !== undefined && { label: 'Red tag value ÷ dispatch value', value: formatPct(q.red_tag_pct_of_dispatch_value) },
            q.total_rework_qty > 0 && { label: 'Rework qty', value: formatNumber(q.total_rework_qty) },
          ].filter(Boolean).map((m) => (
            <div key={m.label} className="border border-[var(--aampl-border)] rounded px-3 py-2 flex items-baseline justify-between gap-2">
              <span className="text-slate-600">{m.label}</span>
              <span className="font-semibold aampl-navy-text">{m.value}</span>
            </div>
          ))}
        </div>
      )}

      {q.entries_missing_price > 0 && (
        <div className="mb-4 px-3 py-2 rounded border border-amber-300 bg-amber-50 text-xs text-amber-900" data-testid="missing-price-banner">
          <AlertTriangle size={12} className="inline mr-1" />
          {q.entries_missing_price} {q.entries_missing_price === 1 ? 'entry' : 'entries'} excluded from COPQ — part price not set.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top rejections */}
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Top 5 Rejections by Cost</div>
          {(q.top_rejections || []).length === 0 ? (
            <div className="text-sm text-slate-500 py-4 text-center bg-slate-50 rounded" data-testid="top-rejections-empty">
              {emptyNote(data, 'No defect-type data.',
                'Defect-level rejection rows are not included in the Operations KPI export — it reports rejection, scrap and COPQ as totals per area only.')}
            </div>
          ) : (
            <div className="aampl-table-wrap">
              <table className="aampl-table text-xs" data-testid="top-rejections-table">
                <thead>
                  <tr>
                    <th>Defect</th>
                    <th className="text-right">Scrap Qty</th>
                    <th className="text-right">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {q.top_rejections.map((r, i) => (
                    <tr key={i}>
                      <td className="font-medium">{r.defect_name}</td>
                      <td className="text-right">{formatNumber(r.qty)}</td>
                      <td className="text-right font-bold">{formatCurrency(r.cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Top parts */}
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Top 5 Parts by COPQ Cost</div>
          {(q.top_parts_by_copq || []).length === 0 ? (
            <div className="text-sm text-slate-500 py-4 text-center bg-slate-50 rounded" data-testid="top-parts-copq-empty">
              {emptyNote(data, 'No part-level COPQ data.',
                'Part-level COPQ rows are not included in the Operations KPI export — its parts master carries no price and its export has no per-part COPQ, so a Top 5 cannot be built from it.')}
            </div>
          ) : (
            <div className="aampl-table-wrap">
              <table className="aampl-table text-xs" data-testid="top-parts-copq-table">
                <thead>
                  <tr>
                    <th>Part</th>
                    <th>Customer</th>
                    <th className="text-right">Scrap</th>
                    <th className="text-right">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {q.top_parts_by_copq.map((p, i) => (
                    <tr key={i}>
                      <td>
                        {p.part_name}
                        <div className="text-[10px] text-slate-500">{p.part_no}</div>
                      </td>
                      <td className="text-xs">{p.customer}</td>
                      <td className="text-right">{formatNumber(p.scrap_qty)}</td>
                      <td className="text-right font-bold">{formatCurrency(p.cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* By department */}
      {q.by_department && Object.keys(q.by_department).length > 0 && (
        <div className="mt-4">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-xs uppercase tracking-wider text-slate-500">By Department</div>
            {data?.source === 'ops_api' && (
              <div className="text-[11px] text-slate-500">
                Rejection is reported plant-wide only — {formatNumber(q.total_rejection_qty)} for this unit
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {Object.entries(q.by_department).map(([dept, agg]) => (
              <div key={dept} className="border border-[var(--aampl-border)] rounded px-3 py-2">
                <div className="text-[11px] uppercase tracking-wider text-slate-500">{dept.replace(/_/g, ' ')}</div>
                <div className="flex items-baseline justify-between mt-1">
                  <span className="text-xs text-slate-600">Rejection</span>
                  <span className="font-semibold">{formatNumber(agg.rejection_qty)}</span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-xs text-slate-600">Scrap</span>
                  <span className="font-semibold">{formatNumber(agg.scrap_qty)}</span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-xs text-slate-600">COPQ</span>
                  <span className="font-bold aampl-navy-text">{formatCurrency(agg.copq)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

// For an API-synced month the Operations export reports plan/actual per shop,
// not per customer. The overall % is (press + machine actual) ÷ (press + machine
// plan) — the same basis as the JSON parser — so those two shop rows are the
// exact inputs to the headline figure and are shown in place of customer rows.
// Completion % per shop is the API's own production_completion_pct, not recomputed.
function shopAdherenceRows(data) {
  const om = data?.ops_metrics;
  if (!isOpsMonth(data) || !om) return [];
  return [
    { shop: 'Press Shop', scheduled: om.press_shop_plan_qty, actual: om.press_shop_actual_qty, adherence_pct: om.press_shop_completion_pct },
    { shop: 'Machine Shop', scheduled: om.machine_shop_plan_qty, actual: om.machine_shop_actual_qty, adherence_pct: om.machine_shop_completion_pct },
  ].filter((r) => r.scheduled || r.actual);
}

function CustomerAdherenceCard({ customerWise, overall, defaultOpen = false, data }) {
  const [open, setOpen] = useState(defaultOpen);
  const cw = customerWise || [];
  const shops = cw.length === 0 ? shopAdherenceRows(data) : [];
  const overallStatus = adherenceTone(overall);
  const countLabel = cw.length > 0
    ? `${cw.length} ${cw.length === 1 ? 'customer' : 'customers'}`
    : shops.length > 0
      ? `${shops.length} shops • customer split not in API export`
      : isOpsMonth(data) ? 'customer split not in API export' : '0 customers';

  return (
    <div className="aampl-card aampl-card-shadow">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid="customer-adherence-toggle"
        className="w-full flex items-center justify-between px-5 py-3 border-b border-[var(--aampl-border)] hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {open ? <ChevronDown size={16} className="text-slate-500" /> : <ChevronRight size={16} className="text-slate-500" />}
          <h3 className="font-semibold aampl-navy-text">
            Schedule Adherence by Customer
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <SectionBadge status={overallStatus} label={`${overall}% overall`} />
          <span className="text-xs text-slate-500" data-testid="customer-adherence-count">{countLabel}</span>
        </div>
      </button>
      {open && (
        <div className="p-5">
          {cw.length === 0 && shops.length > 0 ? (
            <div>
              <div className="aampl-table-wrap">
                <table className="aampl-table" data-testid="shop-adherence-table">
                  <thead>
                    <tr>
                      <th>Shop</th>
                      <th className="text-right">Plan Qty</th>
                      <th className="text-right">Actual Qty</th>
                      <th className="text-right">Completion %</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shops.map((s) => (
                      <tr key={s.shop}>
                        <td className="font-medium">{s.shop}</td>
                        <td className="text-right">{formatNumber(s.scheduled)}</td>
                        <td className="text-right">{formatNumber(s.actual)}</td>
                        <td className="text-right font-bold">{s.adherence_pct ?? '–'}%</td>
                        <td><SectionBadge status={adherenceTone(s.adherence_pct)} label={adherenceTone(s.adherence_pct)} /></td>
                      </tr>
                    ))}
                    <tr style={{ fontWeight: 600 }}>
                      <td>Overall (press + machine)</td>
                      <td className="text-right">{formatNumber(data?.total_scheduled)}</td>
                      <td className="text-right">{formatNumber(data?.total_actual)}</td>
                      <td className="text-right">{overall}%</td>
                      <td><SectionBadge status={overallStatus} label={overallStatus} /></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="text-[11px] text-slate-500 mt-2" data-testid="shop-adherence-note">
                The Operations KPI export reports plan and actual per shop, not per customer. Overall % = (press + machine actual) ÷ (press + machine plan); final inspection is excluded from the denominator, as in the Production JSON parser.
              </div>
            </div>
          ) : cw.length === 0 ? (
            <div className="text-sm text-slate-500 py-4 text-center">{emptyNote(data, 'No customer data.', 'Per-customer plan and actual quantities are not included in the Operations KPI export export.')}</div>
          ) : (
            <div className="aampl-table-wrap">
              <table className="aampl-table" data-testid="customer-adherence-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th className="text-right">Scheduled</th>
                    <th className="text-right">Actual</th>
                    <th className="text-right">Adh %</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {cw.map((c, i) => (
                    <tr key={i}>
                      <td className="font-medium">{c.customer}</td>
                      <td className="text-right">{formatNumber(c.scheduled)}</td>
                      <td className="text-right">{formatNumber(c.actual)}</td>
                      <td className="text-right font-bold">{c.adherence_pct}%</td>
                      <td><SectionBadge status={c.status} label={c.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Production() {
  const { currentCycle, currentUnit } = useCycle();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [parsedAt, setParsedAt] = useState(null);

  useEffect(() => {
    if (!currentCycle || !currentUnit) return;
    setLoading(true);
    api
      .get(`/data/production/${currentCycle.year}/${currentCycle.month}?unit=${currentUnit.code}`)
      .then((r) => {
        // Development trace: the stored Operations response the dashboard renders (same record the PPT reads).
        console.debug('[MRM] OPERATIONS API RESPONSE', `${currentCycle.year}-${currentCycle.month} ${currentUnit.code}`,
          { has_data: r.data?.has_data, source: r.data?.data?.source, quality_entries: r.data?.data?.quality?.quality_entries, rejection_summary: r.data?.data?.quality?.rejection_summary });
        setData(r.data?.data || null);
        setParsedAt(r.data?.parsed_at || null);
      })
      .finally(() => setLoading(false));
  }, [currentCycle, currentUnit]);

  if (!currentUnit || !currentCycle) return <Empty title="No unit/cycle selected" />;
  if (loading) return <Loading />;
  if (!data || !data.customer_wise) {
    return (
      <Empty
        title={`No production data for ${currentUnit.name}`}
        hint="Upload the Production JSON in the Upload Center. Data is split per-unit automatically."
        action={
          <button className="btn-primary" onClick={() => nav('/uploads')} data-testid="go-to-uploads-btn">
            <Upload size={14} className="inline mr-1" /> Go to Upload Center
          </button>
        }
      />
    );
  }

  const bl = data.backlogs || [];
  const tb = data.tool_breakdowns || {};
  const tl = data.tool_life || {};
  const q = data.quality || {};
  const ops = isOpsMonth(data);
  // Signed press-shop backlog exactly as the Operations KPI export reports it
  // (production_backlog_qty; negative = ahead of plan). Not recomputed from
  // plan − actual — the API's figure is on its own cumulative-to-date basis.
  const backlogSigned = ops ? data.ops_metrics?.production_backlog_qty_signed : null;
  const hasBacklogSigned = backlogSigned !== null && backlogSigned !== undefined;

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Production & Operations — ${currentUnit.name}`}
        subtitle={ops
          ? `Uploaded: ${formatDateTime(parsedAt)} • ${data.record_counts?.ops_metrics_ingested || 0} KPI metrics from the Operations KPI export${data.ops_month_to_date ? ' (month to date)' : ''}`
          : `Uploaded: ${formatDateTime(parsedAt)} • ${data.record_counts?.daily_entries_used || 0} daily entries, ${data.record_counts?.cycle_schedules || 0} schedules`}
        sourceBadge={ops ? `Operations KPI export (${currentUnit.code}) — Uploaded` : `Production JSON (${currentUnit.code}) — Uploaded`}
        actions={
          <button className="btn-ghost text-sm" onClick={() => nav('/uploads')} data-testid="reupload-btn">
            Re-upload
          </button>
        }
      />

      {/* 1) Top KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="production-kpis">
        <KPI
          label="Overall Adherence"
          value={`${data.overall_adherence_pct}%`}
          target="98%"
          status={data.overall_adherence_pct >= 95 ? 'green' : data.overall_adherence_pct >= 80 ? 'amber' : 'red'}
        />
        <KPI label="Total Scheduled" value={formatNumber(data.total_scheduled)} status="navy" />
        <KPI label="Total Produced" value={formatNumber(data.total_actual)} status="navy" />
        {hasBacklogSigned ? (
          <KPI
            label="Press Shop Backlog Qty"
            value={backlogSigned < 0 ? `${formatNumber(Math.abs(backlogSigned))} ahead` : formatNumber(backlogSigned)}
            status={backlogSigned > 0 ? 'amber' : 'green'}
          />
        ) : (
          <KPI
            label="Backlog Items"
            value={data.backlog_count}
            status={data.backlog_count > 5 ? 'red' : data.backlog_count > 0 ? 'amber' : 'green'}
          />
        )}
      </div>

      {/* 1b) Shop-wise production (Operations KPI export only) */}
      <ShopWiseCard ops={data.ops_metrics} />

      {/* 1c) Manpower productivity (Operations KPI export only) */}
      {ops && <ManpowerProductivityCard om={data.ops_metrics} />}

      {/* 2) Tool Maintenance Summary */}
      <ToolMaintenanceCard tb={tb} data={data} />

      {/* 3) Tool Life Alerts */}
      <ToolLifeAlertsCard tl={tl} data={data} />

      {/* 4) Quality / COPQ Summary */}
      <QualitySummaryCard q={q} data={data} />

      {/* 4b) Rejection & PPM (Operations KPI export) */}
      <RejectionPpmCard q={q} />

      {/* 5) Schedule Adherence by Customer — collapsible, collapsed by default */}
      <CustomerAdherenceCard
        data={data}
        customerWise={data.customer_wise}
        overall={data.overall_adherence_pct}
        defaultOpen={false}
      />

      {/* 5b) Operations snapshot blocks the KPI export carries (schedule vs supply,
          inventory, hold material, FG planning, raw material, plating, store, objectives) */}
      {ops && <OpsSnapshotSection om={data.ops_metrics} />}

      {/* 6) Production Backlogs */}
      <Card
        title="Production Backlogs"
        action={<span className="text-xs text-slate-500" data-testid="backlog-count">{ops && bl.length === 0 ? 'Line items not in API export' : `${bl.length} items`}</span>}
      >
        {bl.length === 0 && hasBacklogSigned ? (
          <div data-testid="backlog-ops-summary">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-3">
              <KPI
                label="Press Shop Backlog Qty"
                value={backlogSigned < 0 ? `${formatNumber(Math.abs(backlogSigned))} ahead of plan` : formatNumber(backlogSigned)}
                status={backlogSigned > 0 ? 'amber' : 'green'}
              />
              <KPI label="Press Shop Plan Qty" value={formatNumber(data.ops_metrics?.press_shop_plan_qty)} status="navy" />
              <KPI label="Press Shop Actual Qty" value={formatNumber(data.ops_metrics?.press_shop_actual_qty)} status="navy" />
            </div>
            <div className="text-[11px] text-slate-500">
              Backlog qty is the Operations KPI export&apos;s own production_backlog_qty for the press shop (negative = ahead of plan). Per-part backlog line items (schedule, net plan, daily target, cumulative, pending) are not included in the API export.
            </div>
          </div>
        ) : bl.length === 0 ? (
          <div className="text-sm text-slate-500 py-4 text-center">{emptyNote(data, 'No backlogs 🎉', 'Per-part backlog line items are not included in the Operations KPI export export.')}</div>
        ) : (
          <div className="aampl-table-wrap">
            <table className="aampl-table" data-testid="backlogs-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Part</th>
                  <th>Operation</th>
                  <th className="text-right">Backlog</th>
                  <th>Severity</th>
                </tr>
              </thead>
              <tbody>
                {bl.slice(0, 30).map((b, i) => (
                  <tr key={i}>
                    <td className="font-medium">{b.customer}</td>
                    <td className="text-xs">
                      {b.part}
                      <div className="text-[10px] text-slate-500">{b.part_no}</div>
                    </td>
                    <td>{b.operation}</td>
                    <td className="text-right font-bold">{formatNumber(b.backlog_qty)}</td>
                    <td>
                      <SectionBadge status={b.severity} label={b.severity?.toUpperCase()} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
